import { useState, useCallback, useEffect, Suspense, useMemo } from "react";
import { Environment, ContactShadows } from "@react-three/drei";
import { useAuthStore } from "@/stores/authStore";
import {
  useDialogue,
  DialogueBubble,
  CharacterImage,
  DialogueLine,
} from "@/components/VisualNovel";
import { OverlayCanvas } from "./OverlayCanvas";

import { useGUITriangleStore } from "@/stores/guiTriangleStore";

// 캐릭터 이미지 경로
const GUIDE_IMAGE = "/images/guide.png";
const PEOPLE_IMAGES = [
  "/images/people1.png",
  "/images/people2.png",
  "/images/people3.png",
];

export type VNScenario = "intro" | "eviction";

interface VNScenarioOverlayProps {
  scenario: VNScenario | "none";
  tenantData?: {
    id: string;
    username: string;
    text: string;
  };
  modelPath?: string;
  onComplete: (data?: unknown) => void;
  onCancel?: () => void;
}

/**
 * 3D 비주얼 노벨 시나리오 통합 오버레이
 * 인트로(가이드) 및 퇴거(입주민) 상황을 처리합니다.
 */
export function VNScenarioOverlay({
  scenario,
  tenantData,
  modelPath,
  onComplete,
  onCancel,
}: VNScenarioOverlayProps) {
  const {
    signIn,
    signUp,
    signOut,
    anonymousSignIn,
    user,
    getUsername,
    isAnonymous,
  } = useAuthStore();

  const setEnabled = useGUITriangleStore((state) => state.setEnabled);

  // 시나리오 활성화 시 topLeft 삼각형 활성화
  useEffect(() => {
    setEnabled("topLeft", scenario !== "none");
  }, [scenario, setEnabled]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState<string>("start");
  const [error, setError] = useState<string | null>(null);
  const [isReturningUser, setIsReturningUser] = useState(false);

  // 퇴거 시나리오에서 사용할 캐릭터 이미지 인덱스 (tenantData.id 기반으로 결정하여 매번 바뀌도록 함)
  const characterImageIndex = useMemo(() => {
    if (!tenantData?.id) return 0;
    // string id를 숫자로 변환하여 인덱스 추출
    const hash = tenantData.id
      .split("")
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return hash % PEOPLE_IMAGES.length;
  }, [tenantData?.id]);

  // 인트로용 초기 상태 설정
  useEffect(() => {
    if (scenario === "intro" && user && !isAnonymous()) {
      const existingUsername = getUsername();
      if (existingUsername) {
        setUsername(existingUsername);
        setStep("welcome_back");
      }
    }
  }, [scenario, user, isAnonymous, getUsername]);

  // 퇴거 대상이 바뀌면 다이얼로그 단계 초기화 (애니메이션 재재생 효과)
  useEffect(() => {
    if (scenario === "eviction" && tenantData?.id) {
      setStep("start");
    }
  }, [scenario, tenantData?.id]);

  const handleGuestVisit = async () => {
    await signOut();
    setStep("farewell");
    await anonymousSignIn();
  };

  // 대화 시나리오 정의
  const getDialogue = useCallback((): DialogueLine => {
    if (scenario === "eviction") {
      switch (step) {
        case "start":
          return {
            speaker: tenantData?.username || "입주민",
            text: `저를 정말로 내보내실 건가요? "${tenantData?.text}" 메세지도 정말 공들여 썼는데...`,
            choices: [
              { label: "마음이 바뀌었어", action: onCancel },
              {
                label: "미안하지만 나가줘야겠어",
                action: () => setStep("confirm"),
              },
            ],
          };
        case "confirm":
          return {
            speaker: tenantData?.username || "입주민",
            text: "정말인가요? 알겠어요... 짐을 쌀게요.",
            choices: [{ label: "안녕", action: onComplete }],
          };
        default:
          return { speaker: "", text: "" };
      }
    }

    // Intro Scenario (Guide)
    switch (step) {
      case "start":
      case "intro_1":
        return {
          speaker: "CITY GUIDE",
          text: "매일 오르는 집값, 기약 없는 청약 통장... 지친 당신을 위해 준비했습니다.",
        };
      case "intro_2":
        return {
          speaker: "CITY GUIDE",
          text: "현실에서 건물주가 될 수 없다면 디지털 세계는 어떨까요?",
        };
      case "intro_3":
        return {
          speaker: "CITY GUIDE",
          text: "디지털 세계 속 집(ZIP)주인이 되어보세요!",
        };
      case "intro_4":
        return {
          speaker: "CITY GUIDE",
          text: "복잡한 대출이나 청약 없이 건물을 세우고, 누구나 원하는 건물에 자유롭게 세입자가 될 수도 있죠.",
        };
      case "intro_5":
        return {
          speaker: "CITY GUIDE",
          text: "전세사기나 월세 걱정 없는 이곳에서 마음 편히 머물러보세요.",
        };
      case "ask_purpose":
        return {
          speaker: "CITY GUIDE",
          text: "오늘은 어떤 용건으로 오셨나요?",
          choices: [
            {
              label: "새로 입주",
              action: async () => {
                await signOut();
                setIsReturningUser(false);
                setStep("ask_name");
              },
            },
            {
              label: "기존 멤버",
              action: () => {
                if (user && !isAnonymous()) {
                  const existingUsername = getUsername();
                  if (existingUsername) {
                    setUsername(existingUsername);
                    setStep("welcome_back");
                    return;
                  }
                }
                setIsReturningUser(true);
                setStep("ask_name");
              },
            },
            { label: "그냥 구경", action: handleGuestVisit },
          ],
        };
      case "ask_name":
        return {
          speaker: "CITY GUIDE",
          text: "성함을 알려주시겠어요?",
          showInput: "text",
          inputPlaceholder: "이름...",
        };
      case "confirm_name":
        return {
          speaker: "CITY GUIDE",
          text: isReturningUser
            ? "비밀번호를 입력해주세요."
            : "비밀번호를 설정해주세요.",
          showInput: "password",
          inputPlaceholder: "비밀번호...",
        };
      case "processing":
        return { speaker: "CITY GUIDE", text: "확인하고 있어요..." };
      case "welcome_new":
        return {
          speaker: "CITY GUIDE",
          text: "등록 완료! 도시로 가볼까요?",
          choices: [{ label: "입장하기", action: onComplete }],
        };
      case "welcome_back":
        return {
          speaker: "CITY GUIDE",
          text: "어서오세요! 당신의 도시가 기다려요.",
          choices: [{ label: "입장하기", action: onComplete }],
        };
      case "farewell":
        return {
          speaker: "CITY GUIDE",
          text: "편하게 둘러보세요!",
          choices: [{ label: "둘러보기", action: onComplete }],
        };
      default:
        return { speaker: "", text: "" };
    }
  }, [
    scenario,
    step,
    tenantData,
    onCancel,
    onComplete,
    signOut,
    user,
    isAnonymous,
    getUsername,
    isReturningUser,
  ]);

  // ESC 키 처리
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (scenario === "eviction" && onCancel) {
          onCancel();
        } else if (scenario === "intro" && step !== "processing") {
          // 인트로는 특정 단계에서는 취소 가능하도록 (필요 시 수정)
          onCancel?.();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scenario, onCancel, step]);

  const currentDialogue = getDialogue();
  const { displayedText, isTyping, showChoices, showInput, skipTyping } =
    useDialogue(currentDialogue);

  const handleAdvance = useCallback(() => {
    if (isTyping) {
      skipTyping();
      return;
    }

    // 선택지나 입력 필드가 활성화된 상태면 자동 진행 막음
    if (showChoices || showInput) return;

    if (scenario === "eviction") {
      // 퇴거 시나리오는 단계별로 수동 제어 (선택지가 없을 때만 여기 도달)
      if (step === "start") setStep("confirm");
    } else if (scenario === "intro") {
      if (step === "start" || step === "intro_1") setStep("intro_2");
      else if (step === "intro_2") setStep("intro_3");
      else if (step === "intro_3") setStep("intro_4");
      else if (step === "intro_4") setStep("intro_5");
      else if (step === "intro_5") setStep("ask_purpose");
    }
  }, [isTyping, skipTyping, showChoices, showInput, scenario, step]);

  const handleInputSubmit = async (value: string) => {
    if (scenario !== "intro") return;
    setError(null);
    if (step === "ask_name") {
      if (!value.trim()) {
        setError("이름을 입력해주세요.");
        return;
      }
      setUsername(value);
      setStep("confirm_name");
    } else if (step === "confirm_name") {
      if (value.length < 6) {
        setError("6자 이상 입력해주세요.");
        return;
      }
      setPassword(value);
      setStep("processing");
      if (isReturningUser) {
        const { error: signInError } = await signIn(username, value);
        if (signInError) {
          setError("정보 오류.");
          setStep("ask_name");
        } else setStep("welcome_back");
      } else {
        const { error: signUpError } = await signUp(username, value);
        if (signUpError) {
          setError("이미 등록된 이름.");
          setStep("ask_purpose");
        } else setStep("welcome_new");
      }
    }
  };

  return (
    <OverlayCanvas corner="topLeft" zoom={40}>
      <Suspense fallback={null}>
        {/* 전체 클릭 영역 (HTML 입력 필드 제외) */}
        <mesh
          onClick={(e) => {
            e.stopPropagation();
            handleAdvance();
          }}
          visible={false}
        >
          <planeGeometry args={[100, 100]} />
        </mesh>

        <group position={[-2.0, 0, 0]} rotation={[0, Math.PI / 2 - 0.6, 0.0]}>
          <CharacterImage
            imagePath={
              import.meta.env.BASE_URL +
              (scenario === "intro"
                ? GUIDE_IMAGE
                : modelPath || PEOPLE_IMAGES[characterImageIndex])
            }
            position={[-5, -1.4, 0]}
            rotation={[-0.5, 0.0, 0]}
            scale={1.0}
          />

          <DialogueBubble
            dialogue={currentDialogue}
            displayedText={displayedText}
            isTyping={isTyping}
            showChoices={showChoices}
            showInput={showInput}
            inputValue={step === "ask_name" ? username : password}
            onInputSubmit={handleInputSubmit}
            onAdvance={handleAdvance}
            error={error}
            position={[0, 1.4, 0]}
            rotation={[-0.4, 0, 0.42]}
          />

          <ContactShadows
            position={[0, 0, 0]}
            opacity={0.4}
            scale={5}
            blur={2.4}
            far={1}
          />
        </group>
        <Environment preset="city" />
      </Suspense>
    </OverlayCanvas>
  );
}
